/**
 * Course settings · "Contracte": the state of the electronic signing and the
 * sample contract sent with made-up data.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiErrorMessage, apiUrl } from "../../../lib/api";
import { dependencyBadge, failureHint, TONE } from "../../../lib/contractStatus";
import ContractCard, { BTN_PRIMARY, errorDetail, fmtDateTime } from "../ContractCard";
import { readItem, writeItem } from "../../../lib/safeStorage";

export default function ContractSettings() {
  return (
    <div className="grid grid-cols-1 gap-4" data-testid="settings-tab-contracte">
      <ContractsStatusCard />
    </div>
  );
}

/**
 * Logiscool licensing, as it actually is rather than as we assume.
 *
 * The integration plan left several questions open — which pools exist, how
 * much capacity they have, which subjects they can grant, which season is
 * current, and whether our API key authenticates by secret at all. Every one
 * of those is answerable by reading, at no cost, so this card reads them
 * instead of leaving them to be discovered halfway through building the
 * write path.
 *
 * Nothing here writes. Every write in that API consumes a licence.
 */
/**
 * Contract signing: eSemneaza and the R2 archive, checked live.
 *
 * Read-only, like the Logiscool card: nothing is sent and no credit spent.
 * Each row names the Render variable to fix, never its value.
 */
function ContractsStatusCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sample, setSample] = useState(null);
  const [sendingSample, setSendingSample] = useState(false);
  // Who plays the participant. Remembered on this browser only, so the next
  // rehearsal does not need it typed again.
  const [sampleTo, setSampleTo] = useState(() => readItem("local", "contractSampleTo") || "");
  // Which contract to rehearse: the accredited programme's, or the Curs FPED one (12.10.2026).
  const [sampleRegime, setSampleRegime] = useState("acreditat");

  const loadSample = async () => {
    try {
      const r = await api.get("/admin/contracts/sample");
      setSample(r.data?.contract || null);
    } catch {
      setSample(null);
    }
  };

  const sendSample = async () => {
    if (!window.confirm(
      `Trimiți un contract de probă (${sampleRegime === "fped" ? "Curs FPED" : "curs acreditat"}) cu date fictive către ` +
      `${sampleTo.trim() || "adresa ta"}? Consumă 1 credit eSemneaza, ` +
      "iar semnătura semnatarului FPED aplicată la final e reală (documentul poartă marcajul PROBĂ).",
    )) return;
    setSendingSample(true);
    const to = sampleTo.trim();
    writeItem("local", "contractSampleTo", to);
    try {
      const r = await api.post("/admin/contracts/sample", {
        ...(to ? { email: to } : {}), ...(sampleRegime === "fped" ? { regime: "fped" } : {}),
      });
      toast.success(`Contractul de probă ${r.data.contract.number} a plecat către ${r.data.contract.sent_to}.`);
    } catch (err) {
      toast.error(errorDetail(err, "Contractul de probă nu a putut fi trimis."));
    } finally {
      setSendingSample(false);
      loadSample();
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contracts/status");
      setState(r.data);
    } catch (err) {
      setState({ localError: apiErrorMessage(err, "Nu am putut contacta serverul.") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadSample(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [toggling, setToggling] = useState(false);
  const toggleAutoSend = async () => {
    const on = !state?.auto_send?.enabled;
    const question = on
      ? "Pornești trimiterea automată a contractelor? Pleacă la 12 h după ce participantul trimite datele "
        + "suplimentare (sau imediat, dacă grupa începe în sub 72 h), doar între 08:00 și 21:00. Fiecare contract "
        + "consumă 1 credit eSemneaza. Cei care au trimis datele înainte de pornire nu primesc automat: pentru ei "
        + "folosește „Trimite contractele participanților deja plătiți” din Contracte."
      : "Oprești trimiterea automată a contractelor? Contractele deja trimise își păstrează reamintirile.";
    if (!window.confirm(question)) return;
    setToggling(true);
    try {
      await api.put("/admin/contracts/auto-send", { enabled: on });
      toast.success(on ? "Trimiterea automată a contractelor e pornită." : "Trimiterea automată a contractelor e oprită.");
      await load();
    } catch (err) {
      toast.error(errorDetail(err, "Setarea nu a putut fi salvată."));
    } finally {
      setToggling(false);
    }
  };

  const es = state?.esemneaza;
  const canSendSample = Boolean(es?.live_configured && es?.signer_email_configured);
  // One key: FPED works with the production key only (decided 18.09.2026).
  // Probes use it too, so there is no "test key" row to show as missing.
  const rows = state && !state.localError ? [
    {
      key: "live", title: "eSemneaza — cheie API (contul semnatarului)",
      env: "ESEMNEAZA_API_KEY",
      configured: es?.live_configured, ok: es?.live?.ok, error: es?.live?.error,
    },
    {
      key: "signer", title: "eSemneaza — emailul contului semnatar",
      env: "ESEMNEAZA_SIGNER_EMAIL",
      configured: es?.signer_email_configured, ok: es?.signer_email_configured,
      note: "Emailul contului eSemneaza al semnatarului FPED; fără el FPED nu poate semna automat.",
    },
    {
      key: "storage", title: "Arhivă contracte — Cloudflare R2 (UE)",
      env: (state.storage?.missing || []).join(", ") || "R2_*",
      configured: state.storage?.configured, ok: state.storage?.ok, error: state.storage?.error,
    },
  ] : [];

  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="contracts-status-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#373A45]">Semnarea contractelor</h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs font-semibold text-[#965EA7] hover:underline disabled:opacity-50"
        >
          {loading ? "Se verifică…" : "Verifică din nou"}
        </button>
      </div>
      <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
        Doar citire — nu se trimite niciun contract și nu se consumă credite.
      </p>

      {state?.localError && (
        <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2.5 text-sm text-[#6B7280]">
          Nu am putut întreba serverul nostru: <strong>{state.localError}</strong>.
        </div>
      )}
      {state == null && <div className="mt-3 text-sm text-[#6B7280]">Se verifică…</div>}

      <div className="mt-3 divide-y divide-[#E5E7EB]">
        {rows.map((row) => {
          const badge = dependencyBadge(row);
          const hint = failureHint(row.error);
          return (
            <div key={row.key} className="py-2.5" data-testid={`contracts-status-${row.key}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#373A45]">{row.title}</span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONE[badge.tone]}`}>
                  {badge.label}
                </span>
              </div>
              {!row.configured && (
                <div className="mt-1 text-xs text-[#6B7280]">
                  Lipsește din Render: <code>{row.env}</code>{row.note ? ` · ${row.note}` : ""}
                </div>
              )}
              {row.configured && hint && <div className="mt-1 text-xs text-[#9b1f2c]">{hint}</div>}
            </div>
          );
        })}
      </div>

      {state && !state.localError && (
        <div className="mt-3 grid gap-1 text-xs text-[#6B7280]">
          <div>
            Secret webhook (<code>ESEMNEAZA_WEBHOOK_SECRET</code>):{" "}
            <strong className="text-[#373A45]">{es?.webhook_secret_configured ? "setat" : "lipsește"}</strong>
          </div>
          <div data-testid="contracts-webhook">
            Adresa webhook de pus în eSemneaza:{" "}
            <code className="select-all break-all text-[#373A45]">{apiUrl("webhooks/esemneaza")}</code>
            {" "}· ultimul semnal primit:{" "}
            <strong className="text-[#373A45]">
              {state.webhook?.last_received_at ? fmtDateTime(state.webhook.last_received_at) : "niciunul încă"}
            </strong>
          </div>
          <div>
            Semnătura FPED după participant:{" "}
            <strong className="text-[#373A45]">{state.fped_auto_sign ? "automată" : "manuală"}</strong>
            {state.fped_auto_sign ? "" : <> (cea automată e oprită din <code>CONTRACT_FPED_AUTO_SIGN=0</code> în Render)</>}
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="contracts-auto-send">
            <span>
              Trimitere automată a contractelor (la 12 h după datele suplimentare, 08:00–21:00):{" "}
              <strong className={state.auto_send?.enabled ? "text-[#2F6B4E]" : "text-[#373A45]"}>
                {state.auto_send?.enabled ? "pornită" : "oprită"}
              </strong>
              {state.auto_send?.changed_by && (
                <> · schimbat de {state.auto_send.changed_by}, {fmtDateTime(state.auto_send.changed_at)}</>
              )}
            </span>
            <button type="button" onClick={toggleAutoSend} disabled={toggling}
              role="switch" aria-checked={!!state.auto_send?.enabled}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                state.auto_send?.enabled ? "bg-[#965EA7]" : "bg-[#D1D5DB]"}`}
              aria-label="Trimitere automată a contractelor" data-testid="contracts-auto-send-toggle">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                state.auto_send?.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
            {state.auto_send?.pause?.until && new Date(state.auto_send.pause.until) > new Date() && (
              <span className="text-[#9b1f2c]">
                În pauză până la {fmtDateTime(state.auto_send.pause.until)}: {state.auto_send.pause.reason}
              </span>
            )}
          </div>
          <div data-testid="contracts-auto-remind">
            Reamintiri automate (la 2 și 3 zile, 08:00–21:00):{" "}
            <strong className="text-[#373A45]">{state.auto_remind ? "pornite" : "oprite"}</strong>
            {state.auto_remind ? "" : <> (din <code>CONTRACT_AUTO_REMIND=0</code> în Render)</>}
            {" "}· verificarea la 15 minute a rulat ultima dată:{" "}
            <strong className="text-[#373A45]">
              {state.sweep?.at ? fmtDateTime(state.sweep.at) : "niciodată (jobul fped-contracts-sweep din Render)"}
            </strong>
          </div>
          <div>
            Versiune text contract: <strong className="text-[#373A45]">{state.template_version}</strong>
            {state.template_version_fped && (
              <> · Curs FPED: <strong className="text-[#373A45]">{state.template_version_fped}</strong></>
            )}
          </div>
          <div>
            Prag semnătură simplă: <strong className="text-[#373A45]">{state.ses_max_value} lei</strong>
            {" "}(provizoriu; se schimbă din <code>CONTRACT_SES_MAX_VALUE</code>)
          </div>
        </div>
      )}

      {state && !state.localError && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="contracts-sample">
          <div className="text-sm font-semibold text-[#373A45]">Contract de probă cu date fictive</div>
          <p className="mt-1 text-xs text-[#6B7280] leading-relaxed">
            Verifică tot fluxul — trimitere, cod SMS, semnătura FPED, arhivă — fără o înscriere reală și fără
            datele cuiva: „Participant Test”, CNP fictiv, 975 lei. Pleacă la tine, poartă marcajul PROBĂ pe
            fiecare pagină și consumă 1 credit.
          </p>
          <fieldset className="mt-3" data-testid="contracts-sample-regime">
            <legend className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">Care contract</legend>
            <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#373A45]">
              {[["acreditat", "Curs acreditat (contract de formare)"], ["fped", "Curs FPED (contract de participare la curs)"]].map(([value, label]) => (
                <label key={value} className="inline-flex cursor-pointer items-center gap-2">
                  <input type="radio" name="contract-sample-regime" value={value} checked={sampleRegime === value}
                    onChange={() => setSampleRegime(value)} className="h-4 w-4 accent-[#965EA7]"
                    data-testid={`contracts-sample-regime-${value}`} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mt-3 block text-xs font-semibold uppercase tracking-widest text-[#373A45]">
            Trimite către (în locul participantului)
          </label>
          <input
            type="email"
            value={sampleTo}
            onChange={(e) => setSampleTo(e.target.value)}
            placeholder="implicit: adresa ta de login"
            className="mt-1 w-full max-w-md bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#965EA7] outline-none"
            data-testid="contracts-sample-to"
          />
          <p className="mt-1 text-[11px] text-[#6B7280]">
            Trebuie să fie altă adresă decât cea a contului eSemneaza care semnează pentru FPED.
          </p>
          <button type="button" onClick={sendSample} disabled={!canSendSample || sendingSample}
            className={`${BTN_PRIMARY} mt-3`} data-testid="contracts-sample-send"
            title={canSendSample ? "" : "Lipsesc ESEMNEAZA_API_KEY sau ESEMNEAZA_SIGNER_EMAIL în Render."}>
            {sendingSample ? <Loader2 size={14} className="animate-spin" /> : null}
            Trimite un contract de probă
          </button>
          {sample && (
            <ContractCard contract={sample} onChanged={loadSample} testIdPrefix="contracts-sample" />
          )}
        </div>
      )}
    </section>
  );
}
